import { useEffect, useState } from 'react';
import type { CandidateFormAnswerStatus } from '@/types/serializers';
import type AgentApiCandidateFormAnswersAttachedFile from '@/types/serializers/AgentApiCandidateFormAnswersAttachedFile';
import type { StatusTab } from './FormReviewTab';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import { Tag } from '@/components/Tag';
import { LuPaperclip } from 'react-icons/lu';
import CandidateProfileAddForm from '@/features/agent/candidates/components/CandidateProfileAddForm';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import Modal from '@/components/Modal';
import useArchiveCandidateFormAnswer from '@/features/agent/form/api/useArchiveCandidateFormAnswer';
import useFetchCandidateFormAnswer from '@/features/agent/form/api/useFetchCandidateFormAnswer';
import useSyncCandidateFormAnswer from '@/features/agent/form/api/useSyncCandidateFormAnswer';

type View = 'detail' | 'registration';

function getStatusLabel(status: StatusTab): string {
  switch (status) {
    case 'unhandled':
      return '未対応';
    case 'registered':
      return '登録済';
    case 'archived':
      return 'アーカイブ';
  }
}

function answerStatusToTab(status: CandidateFormAnswerStatus): StatusTab {
  switch (status) {
    case 'created':
      return 'unhandled';
    case 'synced':
      return 'registered';
    case 'failed':
      return 'archived';
    default:
      return 'unhandled';
  }
}

function getTagColor(statusTab: StatusTab): 'red' | 'blue' | 'gray' {
  switch (statusTab) {
    case 'unhandled':
      return 'red';
    case 'registered':
      return 'blue';
    case 'archived':
      return 'gray';
  }
}

type FormAnswerItem = { label: string; value: string };

function AttachedFilesPopover({ files }: { files: AgentApiCandidateFormAnswersAttachedFile[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-main"
          aria-label="添付ファイルを表示"
        >
          <LuPaperclip className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="border-b border-solid border-border px-3 py-2 text-xs font-bold text-main">
          添付ファイル
        </div>
        <ul className="max-h-48 overflow-y-auto py-1">
          {files.map((file, index) => (
            <li key={`${file.file_url}-${index}`}>
              <a
                href={file.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 text-sm break-all text-primary hover:bg-gray-50"
              >
                {file.label || file.file_name}
              </a>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

interface Props {
  answerId: number | null;
  open: boolean;
  onClose: () => void;
}

export default function FormReviewDetailModal({ answerId, open, onClose }: Props) {
  const [view, setView] = useState<View>('detail');
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const { data: answer, isLoading, error } = useFetchCandidateFormAnswer(open ? answerId : null);
  const { mutateAsync: archiveAnswer, isPending: isArchiving } = useArchiveCandidateFormAnswer();
  const { mutateAsync: syncAnswer } = useSyncCandidateFormAnswer();

  useEffect(() => {
    if (!open) setView('detail');
  }, [open]);

  const statusTab = answer ? answerStatusToTab(answer.status) : 'unhandled';
  const items = (answer?.candidate_form_answer_items ?? []) as FormAnswerItem[];

  const handleArchive = async () => {
    if (answerId == null) return;
    setIsArchiveConfirmOpen(false);
    await archiveAnswer(answerId);
    onClose();
  };

  const handleRegistrationSuccess = async () => {
    showSuccessToast('求職者を作成しました');
    if (answerId != null) {
      try {
        await syncAnswer(answerId);
      } catch {
        showErrorToast('回答のステータス更新に失敗しました');
      }
    }
    setView('detail');
    onClose();
  };

  return (
    <Modal id="form-review-detail-modal" isShow={open}>
      <div className="flex max-h-[90vh] w-[720px] max-w-[calc(100vw-2rem)] flex-col rounded bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]">
        {/* ヘッダー: view に応じてタイトル切り替え */}
        <div className="flex shrink-0 items-center gap-3 border-b border-solid border-border px-6 py-4">
          <h2 className="text-base font-bold text-main">
            {view === 'detail' ? '求職者情報' : '求職者を登録'}
          </h2>
          {view === 'detail' && answer && (
            <Tag color={getTagColor(statusTab)} size="sm">
              {getStatusLabel(statusTab)}
            </Tag>
          )}
          {view === 'detail' && answer && (answer.attached_files?.length ?? 0) > 0 && (
            <AttachedFilesPopover files={answer.attached_files} />
          )}
        </div>

        {/* コンテンツ: view に応じて詳細 or 登録フォーム */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-4">
          {view === 'detail' && (
            <>
              {isLoading && (
                <div className="py-8">
                  <Loading size="small" />
                </div>
              )}
              {error && (
                <div className="py-8 text-center text-sm text-gray">読み込みに失敗しました</div>
              )}
              {answer && !error && (
                <dl className="flex flex-col gap-4">
                  {items.length === 0 ? (
                    <p className="text-sm text-gray">回答内容がありません</p>
                  ) : (
                    items.map((item, index) => (
                      <div key={index} className="flex flex-col gap-2 py-1">
                        <dt className="text-sm font-bold text-main">{item.label}</dt>
                        <dd className="text-xs font-normal text-main">{item.value || '未入力'}</dd>
                      </div>
                    ))
                  )}
                </dl>
              )}
            </>
          )}
          {view === 'registration' && answer && (
            <CandidateProfileAddForm
              onSuccess={handleRegistrationSuccess}
              onCancel={() => setView('detail')}
              embedInModal
              initialFromAnswer={answer.candidate_form_answer_items}
              candidateFormAnswerId={answer.id}
            />
          )}
        </div>

        {/* フッター: 詳細表示かつ未対応時のみ表示 */}
        {view === 'detail' && answer && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-solid border-border px-6 py-4">
            <Button color="secondary" size="md" onClick={onClose} className="mr-auto">
              閉じる
            </Button>
            {statusTab === 'unhandled' && (
              <>
                <Button
                  color="secondary"
                  size="md"
                  onClick={() => setIsArchiveConfirmOpen(true)}
                  disabled={isArchiving}
                >
                  {isArchiving ? '処理中...' : 'アーカイブ'}
                </Button>
                <Button color="primary" size="md" onClick={() => setView('registration')}>
                  登録に進む
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* アーカイブ確認ダイアログ */}
      <AlertDialog open={isArchiveConfirmOpen} onOpenChange={setIsArchiveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>アーカイブの確認</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="p-lg">
            <AlertDialogDescription>この回答をアーカイブしますか？</AlertDialogDescription>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button color="secondary" size="md">
                キャンセル
              </Button>
            </AlertDialogCancel>
            <Button color="danger" size="md" onClick={handleArchive} disabled={isArchiving}>
              {isArchiving ? '処理中...' : 'アーカイブ'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Modal>
  );
}
